/** Единицы учёта (ТЗ 05 §2.5, BR-105). Базовые: g, ml, pcs. Ввод в ТТК/закупке — любые из UNITS. */
export const BASE_UNITS = ["g", "ml", "pcs"] as const;
export type BaseUnit = (typeof BASE_UNITS)[number];

export const UNITS = {
  g: { base: "g", factor: 1, label: "г" },
  kg: { base: "g", factor: 1000, label: "кг" },
  ml: { base: "ml", factor: 1, label: "мл" },
  l: { base: "ml", factor: 1000, label: "л" },
  pcs: { base: "pcs", factor: 1, label: "шт" },
} as const satisfies Record<string, { base: BaseUnit; factor: number; label: string }>;
export type Unit = keyof typeof UNITS;
export const UNIT_CODES = Object.keys(UNITS) as Unit[];

export class UnitConversionError extends Error {
  constructor(readonly from: string, readonly to: string) {
    super(`Нельзя перевести ${UNITS[from as Unit]?.label ?? from} в ${UNITS[to as Unit]?.label ?? to}`);
  }
}

/**
 * Перевод количества в базовую единицу продукта. г↔мл — только при заданной плотности (г/мл).
 * Бросает UnitConversionError → API отвечает UNIT_CONVERSION_UNDEFINED.
 */
export function toBaseUnit(qty: number, unit: string, productBase: BaseUnit, density?: number | null): number {
  const u = UNITS[unit as Unit];
  if (!u) throw new UnitConversionError(unit, productBase);
  const inUnitBase = qty * u.factor;
  if (u.base === productBase) return inUnitBase;
  if (density && density > 0) {
    if (u.base === "g" && productBase === "ml") return inUnitBase / density;
    if (u.base === "ml" && productBase === "g") return inUnitBase * density;
  }
  throw new UnitConversionError(unit, productBase);
}

/** BR-100: нетто = брутто × (1 − отход%) × (1 − потери при термообработке%). */
export function netQty(gross: number, wastePct: number, cookLossPct: number): number {
  return round4(gross * (1 - wastePct / 100) * (1 - cookLossPct / 100));
}

export const round4 = (n: number) => Math.round(n * 10_000) / 10_000;
export const round2 = (n: number) => Math.round(n * 100) / 100;

/** BR-104: показатели калькуляции. Деньги — minor units; цена — без налогов. */
export function costingMetrics(price: number, cost: number, targetFoodCostPct: number, rounding: number) {
  const foodCostPct = price > 0 ? round2((cost / price) * 100) : null;
  const margin = price - cost;
  const marginPct = price > 0 ? round2((margin / price) * 100) : null;
  const markupPct = cost > 0 ? round2((margin / cost) * 100) : null;
  const raw = targetFoodCostPct > 0 ? cost / (targetFoodCostPct / 100) : 0;
  const recommendedPrice = rounding > 0 ? Math.ceil(raw / rounding) * rounding : Math.ceil(raw);
  return { foodCostPct, margin: Math.round(margin), marginPct, markupPct, recommendedPrice, overTarget: foodCostPct !== null && foodCostPct > targetFoodCostPct };
}
